import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Form,
    FormFeedback,
    Input,
    Label,
    Row,
    Spinner
} from "reactstrap";
import { DashboardPageHeader } from "../../../Components/Common/DashboardKit";
import Flatpickr from "react-flatpickr";
import { useFormik } from "formik";
import * as Yup from "yup";
import SimpleBar from "simplebar-react";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import useAuth from "../../Personal Attendance/useAuth";

// Go-live parity (2026-09-24, S12): legacy TasksNewTask mapped the JCDept
// CODE to its task-list category (several codes share one list) and
// pre-selected the caller's department. Matching the Epicor description to
// the category name reached at most one department per category.
const JCDEPT_TASK_CATEGORY = {
    "19": "HR",
    "82": "Engineering", "83": "Engineering", "84": "Engineering",
    "39": "IT",
    "29": "Accounting & Finance",
    "59": "Quality",
    "1": "Administrative",
    "94": "Shipping", "95": "Shipping",
    "71": "Production", "72": "Production", "73": "Production", "74": "Production",
    "75": "Production", "76": "Production", "99": "Production",
    "69": "Planning & Purchasing",
    "90": "Maintenance", "91": "Maintenance",
    "41": "Sales & Marketing", "49": "Sales & Marketing", "96": "Sales & Marketing"
};
// "01" and "1" are the same code; blanks are trimmed.
const normDeptCode = (code) => {
    const s = String(code ?? "").trim();
    return /^\d+$/.test(s) ? String(parseInt(s, 10)) : s;
};

const STANDARD_TASK_OPTIONS = {
    HR: [
        { value: "Administrative (H1)", label: "Administrative (H1)" },
        { value: "Benefits (H2)", label: "Benefits (H2)" },
        { value: "Compensation (H3)", label: "Compensation (H3)" },
        { value: "Conflict resolution (H4)", label: "Conflict resolution (H4)" },
        { value: "Discipline (H5)", label: "Discipline (H5)" },
        { value: "Employee recognition (H6)", label: "Employee recognition (H6)" },
        { value: "Employee Relations (H7)", label: "Employee Relations (H7)" },
        { value: "Employee separation (H8)", label: "Employee separation (H8)" },
        { value: "Legal (H9)", label: "Legal (H9)" },
        { value: "Managing other locations (H10)", label: "Managing other locations (H10)" },
        { value: "Payroll (H11)", label: "Payroll (H11)" },
        { value: "Performance management (H12)", label: "Performance management (H12)" },
        { value: "Policy issues, handbook, manuals etc. (H13)", label: "Policy issues, handbook, manuals etc. (H13)" },
        { value: "Recruitment (H14)", label: "Recruitment (H14)" },
        { value: "Safety (H15)", label: "Safety (H15)" },
        { value: "Training (H16)", label: "Training (H16)" }
    ],
    Engineering: [
        { value: "Analysis and Reporting (E1)", label: "Analysis and Reporting (E1)" },
        { value: "Build/Test Prototypes (E2)", label: "Build/Test Prototypes (E2)" },
        { value: "Building fixtures/tools (E3)", label: "Building fixtures/tools (E3)" },
        { value: "CNC machining costing (E4)", label: "CNC machining costing (E4)" },
        { value: "Communication (Emails, Calls, etc) (E5)", label: "Communication (Emails, Calls, etc) (E5)" },
        { value: "Contract Reviews (E6)", label: "Contract Reviews (E6)" },
        { value: "Create ATP/FAIR/PPAP (E7)", label: "Create ATP/FAIR/PPAP (E7)" },
        { value: "Create Part Numbers (E8)", label: "Create Part Numbers (E8)" },
        { value: "Create/Revise Model and Drawing (E9)", label: "Create/Revise Model and Drawing (E9)" },
        { value: "Create/Revise MoM/BoM (E10)", label: "Create/Revise MoM/BoM (E10)" },
        { value: "Create/Revise PIR (E11)", label: "Create/Revise PIR (E11)" },
        { value: "Create Time Standards (E12)", label: "Create Time Standards (E12)" },
        { value: "Customer Requested Documents (E13)", label: "Customer Requested Documents (E13)" },
        { value: "Customer support/calls/visits/email/VC (E14)", label: "Customer support/calls/visits/email/VC (E14)" },
        { value: "Design (E15)", label: "Design (E15)" },
        { value: "Design review (E16)", label: "Design review (E16)" },
        { value: "Install new machines/troubleshooting (E17)", label: "Install new machines/troubleshooting (E17)" },
        { value: "List tasks for CM (Salvie) (E18)", label: "List tasks for CM (Salvie) (E18)" },
        { value: "Machinery Maintenance/Upgrade (E19)", label: "Machinery Maintenance/Upgrade (E19)" },
        { value: "MAFIA Verification (E20)", label: "MAFIA Verification (E20)" },
        { value: "Meetings (E21)", label: "Meetings (E21)" },
        { value: "Miscellaneous (E22)", label: "Miscellaneous (E22)" },
        { value: "Monitor Productivity (time study etc) (E23)", label: "Monitor Productivity (time study etc) (E23)" },
        { value: "MRB Rail (E24)", label: "MRB Rail (E24)" },
        { value: "Order Gage (E25)", label: "Order Gage (E25)" },
        { value: "Production Meeting (E26)", label: "Production Meeting (E26)" },
        { value: "RCCA (E27)", label: "RCCA (E27)" },
        { value: "Research and Investigation (E28)", label: "Research and Investigation (E28)" },
        { value: "Resolve DMR's and RMA's (E29)", label: "Resolve DMR's and RMA's (E29)" },
        { value: "Resource Allocation Meeting (RAM) (E30)", label: "Resource Allocation Meeting (RAM) (E30)" },
        { value: "Reverse Engineering? (E31)", label: "Reverse Engineering? (E31)" },
        { value: "Review documents/specs/prints (E32)", label: "Review documents/specs/prints (E32)" },
        { value: "Special Projects (E33)", label: "Special Projects (E33)" },
        { value: "Support SN & UK (different tasks) (E34)", label: "Support SN & UK (different tasks) (E34)" },
        { value: "Training (E35)", label: "Training (E35)" },
        { value: "Troubleshoot on the floor (E36)", label: "Troubleshoot on the floor (E36)" },
        { value: "Vendor communication/research (E37)", label: "Vendor communication/research (E37)" }
    ],
    IT: [
        { value: "Deployment (I1)", label: "Deployment (I1)" },
        { value: "Development (I2)", label: "Development (I2)" },
        { value: "Evaluation (I3)", label: "Evaluation (I3)" },
        { value: "Integration & Testing (I4)", label: "Integration & Testing (I4)" },
        { value: "Maintenance (I5)", label: "Maintenance (I5)" },
        { value: "Network Diagnosis (I6)", label: "Network Diagnosis (I6)" },
        { value: "PC Diagnosis (I7)", label: "PC Diagnosis (I7)" },
        { value: "Preliminary Analysis (I8)", label: "Preliminary Analysis (I8)" },
        { value: "Requirements Definition (I9)", label: "Requirements Definition (I9)" },
        { value: "Systems Design (I10)", label: "Systems Design (I10)" },
        { value: "Training (I11)", label: "Training (I11)" },
        { value: "Upgrades (I12)", label: "Upgrades (I12)" },
        { value: "Meetings (I13)", label: "Meetings (I13)" }
    ],
    "Accounting & Finance": [
        { value: "Apply Cash/Check (F1)", label: "Apply Cash/Check (F1)" },
        { value: "Calculate Tax Payable (F2)", label: "Calculate Tax Payable (F2)" },
        { value: "Collection Efforts (F3)", label: "Collection Efforts (F3)" },
        { value: "Cut Checks (F4)", label: "Cut Checks (F4)" },
        { value: "Deposit Checks at Bank (F5)", label: "Deposit Checks at Bank (F5)" },
        { value: "Deposit Checks On Site (F6)", label: "Deposit Checks On Site (F6)" },
        { value: "Enter AP Invoice (F7)", label: "Enter AP Invoice (F7)" },
        { value: "Financial Reports (F8)", label: "Financial Reports (F8)" },
        { value: "Payroll GL Entry (F9)", label: "Payroll GL Entry (F9)" },
        { value: "Reconcile AR Deposits (F10)", label: "Reconcile AR Deposits (F10)" },
        { value: "Reconcile Bank Account (F11)", label: "Reconcile Bank Account (F11)" },
        { value: "Reconcile Checks (F12)", label: "Reconcile Checks (F12)" },
        { value: "Scan AP Invoice (F13)", label: "Scan AP Invoice (F13)" },
        { value: "Standard Cost Verification (F14)", label: "Standard Cost Verification (F14)" },
        { value: "System Entry Verification (F15)", label: "System Entry Verification (F15)" },
        { value: "Work with Auditors (F16)", label: "Work with Auditors (F16)" }
    ],
    Quality: [
        { value: "ATP/FAIR (Q1)", label: "ATP/FAIR (Q1)" },
        { value: "CA/PA (Q2)", label: "CA/PA (Q2)" },
        { value: "Customer Support (Q3)", label: "Customer Support (Q3)" },
        { value: "Gages (Q4)", label: "Gages (Q4)" },
        { value: "Inspection (Q5)", label: "Inspection (Q5)" },
        { value: "MRB/DMR (Q6)", label: "MRB/DMR (Q6)" },
        { value: "Process & Document Control (Q7)", label: "Process & Document Control (Q7)" },
        { value: "RMA (Q8)", label: "RMA (Q8)" },
        { value: "Meetings (Q9)", label: "Meetings (Q9)" }
    ],
    Production: [
        { value: "Assist/ correct programs SN (C1)", label: "Assist/ correct programs SN (C1)" },
        { value: "Conversation/Discussion: FL (C2)", label: "Conversation/Discussion: FL (C2)" },
        { value: "Correct existing programs: FL (C3)", label: "Correct existing programs: FL (C3)" },
        { value: "Fixture Design/ Assistance: SN (C4)", label: "Fixture Design/ Assistance: SN (C4)" },
        { value: "Fixture Design: FL (C5)", label: "Fixture Design: FL (C5)" },
        { value: "Tool Order/Research (C6)", label: "Tool Order/Research (C6)" },
        { value: "Troubleshoot/explain set-up (C7)", label: "Troubleshoot/explain set-up (C7)" },
        { value: "Write E-mails (C8)", label: "Write E-mails (C8)" },
        { value: "Write New Programs (C9)", label: "Write New Programs (C9)" },
        { value: "Enter/Verify Labor reporting (C10)", label: "Enter/Verify Labor reporting (C10)" },
        { value: "Verify material in stock for operation (C11)", label: "Verify material in stock for operation (C11)" },
        { value: "Order materials for production (C12)", label: "Order materials for production (C12)" },
        { value: "Meetings, interviews and tours (C13)", label: "Meetings, interviews and tours (C13)" },
        { value: "Production Meeting (C14)", label: "Production Meeting (C14)" }
    ],
    Administrative: [
        { value: "Discussion / conversation at FL plant (A1)", label: "Discussion / conversation at FL plant (A1)" },
        { value: "Expediting Activities (A2)", label: "Expediting Activities (A2)" },
        { value: "Job suggestions and job creation (A3)", label: "Job suggestions and job creation (A3)" },
        { value: "Phone conversation (A4)", label: "Phone conversation (A4)" },
        { value: "PO suggestions and PO creation (A5)", label: "PO suggestions and PO creation (A5)" },
        { value: "Subcontract documents creation (A6)", label: "Subcontract documents creation (A6)" },
        { value: "Town Hall (A7)", label: "Town Hall (A7)" },
        { value: "Video Conference India (A8)", label: "Video Conference India (A8)" },
        { value: "Web search (A9)", label: "Web search (A9)" },
        { value: "Write E-mails (A10)", label: "Write E-mails (A10)" }
    ],
    "Planning & Purchasing": [
        { value: "Attend Meeting (P1)", label: "Attend Meeting (P1)" },
        { value: "Create Purchase Orders (P2)", label: "Create Purchase Orders (P2)" },
        { value: "Release jobs in Epicor (P3)", label: "Release jobs in Epicor (P3)" },
        { value: "Resolve any open issues or DMR with suppliers (P4)", label: "Resolve any open issues or DMR with suppliers (P4)" },
        { value: "Review job status and expedite (P5)", label: "Review job status and expedite (P5)" },
        { value: "Review Open customer orders (P6)", label: "Review Open customer orders (P6)" },
        { value: "Review production demand using Utility and epicor workbench (P7)", label: "Review production demand using Utility and epicor workbench (P7)" },
        { value: "Review Purchase needs using Buyer Workbench (P8)", label: "Review Purchase needs using Buyer Workbench (P8)" },
        { value: "Supplier communications (P9)", label: "Supplier communications (P9)" },
        { value: "Write emails  (P10)", label: "Write emails  (P10)" },
        { value: "Pack slip creation for regular shipments (P11)", label: "Pack slip creation for regular shipments (P11)" },
        { value: "Job suggestions and job creation (P12)", label: "Job suggestions and job creation (P12)" }
    ],
    Shipping: [
        { value: "Checking and Revising India shipments received (S1)", label: "Checking and Revising India shipments received (S1)" },
        { value: "Cycle Counting for Inventory (S2)", label: "Cycle Counting for Inventory (S2)" },
        { value: "Daily Orders Processing (S3)", label: "Daily Orders Processing (S3)" },
        { value: "Emails – Sending and Receiving emails (S4)", label: "Emails – Sending and Receiving emails (S4)" },
        { value: "Freight Invoicing (S5)", label: "Freight Invoicing (S5)" },
        { value: "India Logistics - Meetings and Issues (S6)", label: "India Logistics - Meetings and Issues (S6)" },
        { value: "Meetings , Training, Seminars, etc.  (S7)", label: "Meetings , Training, Seminars, etc.  (S7)" },
        { value: "Misc. fill in work for person out on FTO (S8)", label: "Misc. fill in work for person out on FTO (S8)" },
        { value: "Order processing - AES, ASN on Cust. Website, Invoicing, scheduling pickups, BOL’s, SLI’s (S9)", label: "Order processing - AES, ASN on Cust. Website, Invoicing, scheduling pickups, BOL’s, SLI’s (S9)" },
        { value: "Scanning daily misc. and subcontracts PS’S and received items documents (S10)", label: "Scanning daily misc. and subcontracts PS’S and received items documents (S10)" },
        { value: "Supplies- ordering supplies for Dept. and Facility restrooms - cleaning (S11)", label: "Supplies- ordering supplies for Dept. and Facility restrooms - cleaning (S11)" }
    ],
    Maintenance: [
        { value: "Repair AC systems (M1)", label: "Repair AC systems (M1)" },
        { value: "Repair Walls (M2)", label: "Repair Walls (M2)" },
        { value: "Repair Ceilings (M3)", label: "Repair Ceilings (M3)" },
        { value: "Repair production equipment (M4)", label: "Repair production equipment (M4)" },
        { value: "Repair building equipment (M5)", label: "Repair building equipment (M5)" },
        { value: "Repair CNC machines (M6)", label: "Repair CNC machines (M6)" },
        { value: "Repair fork lifts (M7)", label: "Repair fork lifts (M7)" },
        { value: "Research and documentation (M8)", label: "Research and documentation (M8)" },
        { value: "Conducting repair assessments (M9)", label: "Conducting repair assessments (M9)" },
        { value: "Working on grounds (M10)", label: "Working on grounds (M10)" },
        { value: "Meetings - dept, vendors and internal customers (M11)", label: "Meetings - dept, vendors and internal customers (M11)" },
        { value: "TPM  (M12)", label: "TPM  (M12)" }
    ],
    "Sales & Marketing": [
        { value: "Receive PO/Create SOs (Z1)", label: "Receive PO/Create SOs (Z1)" },
        { value: "Quoting of new programs (Z2)", label: "Quoting of new programs (Z2)" },
        { value: "Submit RFQs/Proposals/Quotes to customers (Z3)", label: "Submit RFQs/Proposals/Quotes to customers (Z3)" },
        { value: "Issue and process RMAs (Z4)", label: "Issue and process RMAs (Z4)" },
        { value: "Past Due Order Management (Z5)", label: "Past Due Order Management (Z5)" },
        { value: "Customer drawings and requirements review (Z6)", label: "Customer drawings and requirements review (Z6)" },
        { value: "New Business Development (Z7)", label: "New Business Development (Z7)" },
        { value: "Customer surveys (Z8)", label: "Customer surveys (Z8)" },
        { value: "Customer calls and meetings (Z9)", label: "Customer calls and meetings (Z9)" },
        { value: "Contract reviews (Z10)", label: "Contract reviews (Z10)" },
        { value: "Special projects (Z11)", label: "Special projects (Z11)" },
        { value: "Review/Implement NDAs (Z12)", label: "Review/Implement NDAs (Z12)" },
        { value: "Market Research/Marketing (Z13)", label: "Market Research/Marketing (Z13)" },
        { value: "Customer Requested Documents (Z14)", label: "Customer Requested Documents (Z14)" },
        { value: "Meetings (Z15)", label: "Meetings (Z15)" }
    ]
};
const CreateTask = () => {
    document.title = "Create New Task | SSW Technologies Dashboard";

    const navigate = useNavigate();

    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState("");
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");

    const authUser = JSON.parse(sessionStorage.getItem("authUser"));
    const currentEmpId = authUser?.uid;
    const { department: myDeptCode } = useAuth();

    // Go-live parity (2026-09-24, S12): the caller's own department is
    // pre-selected (legacy UserDB.GetDept → cboDept).
    const defaultDepartment = useMemo(() => {
        const mine = normDeptCode(myDeptCode);
        if (!mine) return "";
        const hit = departments.find((d) => normDeptCode(d.id) === mine);
        return hit ? String(hit.id) : "";
    }, [departments, myDeptCode]);

    useEffect(() => {
        const loadLookups = async () => {
            try {
                setLookupLoading(true);
                setLookupError("");

                const [departmentsRes, employeesRes] = await Promise.all([
                    api.get("/registration/departments"),
                    api.get("/WhosInBuilding/userList")
                ]);

                const departmentsData = departmentsRes?.data ?? departmentsRes ?? [];
                const employeesData = employeesRes?.data ?? employeesRes ?? [];

                setDepartments(Array.isArray(departmentsData) ? departmentsData : []);
                setEmployees(
                    Array.isArray(employeesData)
                        ? employeesData.filter((emp) => emp.status === "A")
                        : []
                );
            } catch (error) {
                console.error("Lookup load error:", error);
                setLookupError("Unable to load departments or employee list.");
            } finally {
                setLookupLoading(false);
            }
        };

        loadLookups();
    }, []);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            taskMode: "Standard",
            department: defaultDepartment,   // Go-live parity (2026-09-24, S12)
            taskDescription: "",
            customTitle: "",
            extraText: "",
            assignMode: "Myself",
            pertinentInfo: "",
            needReceipt: false,
            immediatelyInitiate: false,
            emailWhenComplete: false
        },
        validationSchema: Yup.object({
            taskMode: Yup.string().required("Please select task mode"),
            department: Yup.string().required("Please select department"),
            taskDescription: Yup.string().when("taskMode", {
                is: "Standard",
                then: (schema) => schema.required("Please enter or select a task"),
                otherwise: (schema) => schema.notRequired()
            }),
            customTitle: Yup.string().when("taskMode", {
                is: "Custom",
                then: (schema) => schema.required("Please enter custom task title"),
                otherwise: (schema) => schema.notRequired()
            }),
            assignMode: Yup.string().required("Please select assign mode")
        }),
        onSubmit: async (values) => {
            setSubmitError("");
            setSubmitSuccess("");

            if (!currentEmpId) {
                setSubmitError("Employee ID was not found in session.");
                return;
            }

            if (values.assignMode === "Specific" && selectedEmployees.length < 1) {
                validation.setFieldError("assignMode", "Please select at least one person");
                return;
            }

            const finalTitle =
                values.taskMode === "Custom"
                    ? values.extraText?.trim()
                        ? `${values.customTitle.trim()} - ${values.extraText.trim()}`
                        : values.customTitle.trim()
                    : values.extraText?.trim()
                        ? `${getTaskDescriptionLabel(values.taskDescription, departmentTaskOptions)} - ${values.extraText.trim()}`
                        : getTaskDescriptionLabel(values.taskDescription, departmentTaskOptions);

            if (!finalTitle?.trim()) {
                setSubmitError("Please enter the task title.");
                return;
            }

            // Round DMS-IssueRound-1c (owner): the picker is the CREATED date
            // (defaults to today; a past date is allowed — the task may be
            // logged after the fact). No due date is set any more.
            const pickedDate = new Date(selectedDate);
            if (Number.isNaN(pickedDate.getTime())) {
                setSubmitError("Please pick a valid created date.");
                return;
            }
            pickedDate.setHours(0, 0, 0, 0);

            try {
                setSubmitting(true);

                const attachments = await Promise.all(
                    files.map(async (file) => ({
                        fileName: file.name,
                        rawFile: await toBase64(file)
                    }))
                );

                const payload = {
                    createdByEmpId: String(currentEmpId),
                    taskMode: values.taskMode,
                    departmentId: values.department,
                    taskDescription: values.taskDescription || "",
                    customTitle: values.customTitle || "",
                    extraText: values.extraText || "",
                    finalTitle,
                    // Round DMS-IssueRound-1c — created date replaces due date
                    // (local calendar date, not a UTC instant, so the stored
                    // day never shifts).
                    createdDate: `${pickedDate.getFullYear()}-${String(pickedDate.getMonth() + 1).padStart(2, "0")}-${String(pickedDate.getDate()).padStart(2, "0")}`,
                    dueDate: null,
                    assignMode: values.assignMode,
                    assignedTo:
                        values.assignMode === "Myself"
                            ? [String(currentEmpId)]
                            : selectedEmployees.map((x) => String(x.empId)),
                    pertinentInfo: values.pertinentInfo || "",
                    needReceipt: false,   // Go-live parity (2026-09-24, S1): no longer used
                    immediatelyInitiate: values.immediatelyInitiate,
                    emailWhenComplete: values.emailWhenComplete,
                    attachments
                };

                const response = await api.post("/TaskManagement/create-task", payload);

                const responseData = response?.data ?? response;
                const createdTaskId = responseData?.taskId;

                setSubmitSuccess("Task successfully created.");

                validation.resetForm();
                setSelectedEmployees([]);
                setFiles([]);
                setSelectedDate(new Date());

                // Round TM-5 (2026) — legacy "Immediately Initiate Task":
                // legacy opened the labor window for the new task right
                // away; modern jumps straight to the task page where the
                // Start button (and the one-open-session confirm flow)
                // lives.
                if (values.immediatelyInitiate && createdTaskId) {
                    navigate(`/task-management/view-task/${createdTaskId}`);
                }
            } catch (error) {
                console.error("Create task error:", error);
                setSubmitError(
                    error?.response?.data?.message ||
                    error?.response?.data ||
                    "Failed to create task."
                );
            } finally {
                setSubmitting(false);
            }
        }
    });

    const selectedDepartmentName = useMemo(() => {
        const selectedDepartment = departments.find(
            (d) => String(d.id) === String(validation.values.department)
        );

        return selectedDepartment?.description?.trim() || "";
    }, [departments, validation.values.department]);

    // Go-live parity (2026-09-24, S12): the list is chosen by the JCDept
    // CODE first (legacy mapping); the description match stays as fallback.
    const departmentTaskOptions = useMemo(() => {
        const category = JCDEPT_TASK_CATEGORY[normDeptCode(validation.values.department)];
        return STANDARD_TASK_OPTIONS[category] || STANDARD_TASK_OPTIONS[selectedDepartmentName] || [];
    }, [selectedDepartmentName, validation.values.department]);

    const isManualTaskDepartment =
        validation.values.taskMode === "Standard" && departmentTaskOptions.length === 0;

    const finalTaskTitle = useMemo(() => {
        if (validation.values.taskMode === "Custom") {
            if (!validation.values.customTitle?.trim()) return "";
            return validation.values.extraText?.trim()
                ? `${validation.values.customTitle.trim()} - ${validation.values.extraText.trim()}`
                : validation.values.customTitle.trim();
        }

        const selectedLabel = getTaskDescriptionLabel(
            validation.values.taskDescription,
            departmentTaskOptions
        );

        if (!selectedLabel) return "";

        return validation.values.extraText?.trim()
            ? `${selectedLabel} - ${validation.values.extraText.trim()}`
            : selectedLabel;
    }, [
        validation.values.taskMode,
        validation.values.taskDescription,
        validation.values.customTitle,
        validation.values.extraText,
        departmentTaskOptions
    ]);

    const handleAddEmployee = (empId) => {
        const selected = employees.find((x) => String(x.empId) === String(empId));
        if (!selected) return;

        const alreadyExists = selectedEmployees.some((x) => x.empId === selected.empId);
        if (alreadyExists) return;

        setSelectedEmployees((prev) => [...prev, selected]);
    };

    const handleRemoveEmployee = (empId) => {
        setSelectedEmployees((prev) => prev.filter((x) => x.empId !== empId));
    };

    const handleFileChange = (e) => {
        const incomingFiles = Array.from(e.target.files || []);
        setFiles(incomingFiles);
    };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-add-circle-line"
                    title="Create New Task"
                    subtitle="Define a new task and assign owners"
                    tone="default"
                    breadcrumb={[
                        { label: "Task Management", to: "/task-management" },
                        { label: "Create New Task" },
                    ]}
                />

                {lookupError && (
                    <Alert color="danger" className="mb-3">
                        {lookupError}
                    </Alert>
                )}

                {submitError && (
                    <Alert color="danger" className="mb-3">
                        {submitError}
                    </Alert>
                )}

                {submitSuccess && (
                    <Alert color="success" className="mb-3">
                        {submitSuccess}
                    </Alert>
                )}

                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                    }}
                >
                    <Row>
                        <Col xl={8}>
                            <Card className="shadow-sm border-0">
                                <CardBody>
                                    {lookupLoading ? (
                                        <div className="text-center py-5">
                                            <Spinner color="primary" />
                                            <div className="mt-2 text-muted">
                                                Loading departments and employees...
                                            </div>
                                        </div>
                                    ) : (
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Label className="form-label">Task Mode</Label>
                                                <Input
                                                    type="select"
                                                    name="taskMode"
                                                    value={validation.values.taskMode}
                                                    onChange={validation.handleChange}
                                                    onBlur={validation.handleBlur}
                                                    invalid={validation.touched.taskMode && !!validation.errors.taskMode}
                                                >
                                                    <option value="Standard">Standard</option>
                                                    <option value="Custom">Custom</option>
                                                </Input>
                                                {validation.touched.taskMode && validation.errors.taskMode ? (
                                                    <FormFeedback>{validation.errors.taskMode}</FormFeedback>
                                                ) : null}
                                            </Col>

                                            <Col md={6}>
                                                <Label className="form-label">Select Dept</Label>
                                                <Input
                                                    type="select"
                                                    name="department"
                                                    value={validation.values.department}
                                                    onChange={(e) => {
                                                        validation.handleChange(e);
                                                        validation.setFieldValue("taskDescription", "");
                                                    }}
                                                    onBlur={validation.handleBlur}
                                                    invalid={validation.touched.department && !!validation.errors.department}
                                                >
                                                    <option value="">Select Department</option>
                                                    {departments.map((dept) => (
                                                        <option key={dept.id} value={dept.id}>
                                                            {dept.description}
                                                        </option>
                                                    ))}
                                                </Input>
                                                {validation.touched.department && validation.errors.department ? (
                                                    <FormFeedback>{validation.errors.department}</FormFeedback>
                                                ) : null}
                                            </Col>

                                            {validation.values.taskMode === "Standard" && !isManualTaskDepartment ? (
                                                <Col md={12}>
                                                    <Label className="form-label">Select Task</Label>
                                                    <Input
                                                        type="select"
                                                        name="taskDescription"
                                                        value={validation.values.taskDescription}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        invalid={
                                                            validation.touched.taskDescription &&
                                                            !!validation.errors.taskDescription
                                                        }
                                                    >
                                                        <option value="">Select task</option>
                                                        {departmentTaskOptions.map((task) => (
                                                            <option key={task.value} value={task.value}>
                                                                {task.label}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                    {validation.touched.taskDescription &&
                                                        validation.errors.taskDescription ? (
                                                        <FormFeedback>{validation.errors.taskDescription}</FormFeedback>
                                                    ) : null}
                                                </Col>
                                            ) : validation.values.taskMode === "Standard" ? (
                                                <Col md={12}>
                                                    <Label className="form-label">Task Name</Label>
                                                    <Input
                                                        type="text"
                                                        name="taskDescription"
                                                        placeholder="Enter task name manually"
                                                        value={validation.values.taskDescription}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        invalid={
                                                            validation.touched.taskDescription &&
                                                            !!validation.errors.taskDescription
                                                        }
                                                    />
                                                    <div className="form-text">
                                                        No predefined task list for this department. Enter task name manually.
                                                    </div>
                                                    {validation.touched.taskDescription &&
                                                        validation.errors.taskDescription ? (
                                                        <FormFeedback>{validation.errors.taskDescription}</FormFeedback>
                                                    ) : null}
                                                </Col>
                                            ) : (
                                                <Col md={12}>
                                                    <Label className="form-label">Custom Task Title</Label>
                                                    <Input
                                                        type="text"
                                                        name="customTitle"
                                                        placeholder="Enter custom task title"
                                                        value={validation.values.customTitle}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        invalid={
                                                            validation.touched.customTitle &&
                                                            !!validation.errors.customTitle
                                                        }
                                                    />
                                                    {validation.touched.customTitle &&
                                                        validation.errors.customTitle ? (
                                                        <FormFeedback>{validation.errors.customTitle}</FormFeedback>
                                                    ) : null}
                                                </Col>
                                            )}

                                            <Col md={8}>
                                                <Label className="form-label">Additional Title Text</Label>
                                                <Input
                                                    type="text"
                                                    name="extraText"
                                                    placeholder="Optional extra text"
                                                    value={validation.values.extraText}
                                                    onChange={validation.handleChange}
                                                />
                                            </Col>

                                            <Col md={4}>
                                                <Label className="form-label">Created date</Label>
                                                <Flatpickr
                                                    className="form-control"
                                                    value={selectedDate}
                                                    options={{
                                                        altInput: true,
                                                        altFormat: "F j, Y",
                                                        dateFormat: "Y-m-d"
                                                    }}
                                                    onChange={(date) => setSelectedDate(date[0])}
                                                />
                                            </Col>

                                            <Col md={12}>
                                                <Label className="form-label">Assign To</Label>
                                                <div className="d-flex gap-4 mt-1">
                                                    <div className="form-check">
                                                        <Input
                                                            type="radio"
                                                            className="form-check-input"
                                                            id="assign-myself"
                                                            name="assignMode"
                                                            value="Myself"
                                                            checked={validation.values.assignMode === "Myself"}
                                                            onChange={validation.handleChange}
                                                        />
                                                        <Label className="form-check-label" htmlFor="assign-myself">
                                                            Myself
                                                        </Label>
                                                    </div>

                                                    <div className="form-check">
                                                        <Input
                                                            type="radio"
                                                            className="form-check-input"
                                                            id="assign-specific"
                                                            name="assignMode"
                                                            value="Specific"
                                                            checked={validation.values.assignMode === "Specific"}
                                                            onChange={validation.handleChange}
                                                        />
                                                        <Label className="form-check-label" htmlFor="assign-specific">
                                                            Specific Person / People
                                                        </Label>
                                                    </div>
                                                </div>

                                                {validation.errors.assignMode ? (
                                                    <div className="text-danger small mt-1">
                                                        {validation.errors.assignMode}
                                                    </div>
                                                ) : null}
                                            </Col>

                                            {validation.values.assignMode === "Specific" && (
                                                <>
                                                    <Col md={8}>
                                                        <Label className="form-label">Select Employee</Label>
                                                        <Input
                                                            type="select"
                                                            onChange={(e) => {
                                                                if (e.target.value) handleAddEmployee(e.target.value);
                                                            }}
                                                        >
                                                            <option value="">Choose person</option>
                                                            {employees.map((emp) => (
                                                                <option key={emp.empId} value={emp.empId}>
                                                                    {emp.name}{emp.title ? ` - ${emp.title}` : ""}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </Col>

                                                    <Col md={4} className="d-flex align-items-end">
                                                        <div className="w-100 text-muted small">
                                                            {selectedEmployees.length} selected
                                                        </div>
                                                    </Col>

                                                    <Col md={12}>
                                                        <div className="border rounded p-2" style={{ minHeight: 80 }}>
                                                            {selectedEmployees.length === 0 ? (
                                                                <span className="text-muted small">
                                                                    No employees selected
                                                                </span>
                                                            ) : (
                                                                <SimpleBar style={{ maxHeight: "110px" }}>
                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        {selectedEmployees.map((emp) => (
                                                                            <Badge
                                                                                key={emp.empId}
                                                                                color="primary"
                                                                                className="d-flex align-items-center gap-2 px-3 py-2"
                                                                            >
                                                                                <span>{emp.name}</span>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn-close btn-close-sm"
                                                                                    aria-label="Remove"
                                                                                    onClick={() => handleRemoveEmployee(emp.empId)}
                                                                                />
                                                                            </Badge>
                                                                        ))}
                                                                    </div>
                                                                </SimpleBar>
                                                            )}
                                                        </div>
                                                    </Col>
                                                </>
                                            )}

                                            <Col md={12}>
                                                <Label className="form-label">Pertinent Info</Label>
                                                <Input
                                                    type="text"
                                                    name="pertinentInfo"
                                                    placeholder="job number, part number, etc."
                                                    value={validation.values.pertinentInfo}
                                                    onChange={validation.handleChange}
                                                />
                                            </Col>

                                            <Col md={12}>
                                                <Label className="form-label">Attachment</Label>
                                                <Input type="file" multiple onChange={handleFileChange} />
                                                {files.length > 0 && (
                                                    <div className="mt-2 small text-muted">
                                                        {files.length} file(s) selected
                                                    </div>
                                                )}
                                            </Col>

                                            <Col md={12}>
                                                <div className="d-flex flex-column gap-2 mt-2">
                                                    <div className="form-check">
                                                        {/* Go-live parity (2026-09-24, S1): legacy had ONE box
                                                            (chkEmpReceipt → Tasks.emailWhenComplete). This labelled
                                                            box now sets emailWhenComplete; the duplicate "Email when
                                                            complete" box is folded into it. Supervisor receipts
                                                            follow the manager's own option only. */}
                                                        <Input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id="emailWhenComplete"
                                                            name="emailWhenComplete"
                                                            checked={validation.values.emailWhenComplete}
                                                            onChange={validation.handleChange}
                                                        />
                                                        <Label className="form-check-label" htmlFor="emailWhenComplete">
                                                            Send me an email when assignee(s) complete the task
                                                        </Label>
                                                    </div>

                                                    <div className="form-check">
                                                        <Input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id="immediatelyInitiate"
                                                            name="immediatelyInitiate"
                                                            checked={validation.values.immediatelyInitiate}
                                                            onChange={validation.handleChange}
                                                        />
                                                        <Label className="form-check-label" htmlFor="immediatelyInitiate">
                                                            Initiate task reporting immediately following task creation
                                                        </Label>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={4}>
                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-white border-0">
                                    <h5 className="mb-0">Task Preview</h5>
                                </CardHeader>
                                <CardBody>
                                    <div className="mb-3">
                                        <div className="text-muted small mb-1">Final Task Title</div>
                                        <div className="fw-semibold">
                                            {finalTaskTitle || "Task title preview will appear here"}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="text-muted small mb-1">Created date</div>
                                        <div className="fw-semibold">
                                            {selectedDate ? new Date(selectedDate).toLocaleDateString() : "-"}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="text-muted small mb-1">Assigned To</div>
                                        <div className="fw-semibold">
                                            {validation.values.assignMode === "Myself"
                                                ? "Myself"
                                                : selectedEmployees.length > 0
                                                    ? selectedEmployees.map((x) => x.name).join(", ")
                                                    : "No one selected"}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="text-muted small mb-1">Attachments</div>
                                        <div className="fw-semibold">
                                            {files.length > 0 ? `${files.length} file(s) selected` : "No attachments"}
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2 pt-2">
                                        <Button
                                            type="button"
                                            color="light"
                                            className="w-50"
                                            onClick={() => navigate("/task-management")}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            color="primary"
                                            className="w-50"
                                            disabled={submitting || lookupLoading}
                                        >
                                            {submitting ? "Submitting..." : "Submit"}
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </Container>
        </div>
    );
};

function getTaskDescriptionLabel(selectedValue, options) {
    const item = (options || []).find((x) => x.value === selectedValue);
    return item?.label || selectedValue || "";
}

function toBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result || "";
            const base64 = String(result).split(",")[1];
            resolve(base64);
        };
        reader.onerror = (error) => reject(error);
    });
}

export default CreateTask;